import GolfIcon from './GolfIcon';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, User, LogOut, Plus, MessageCircle, Bell, MailWarning, ShieldCheck } from 'lucide-react';
import { notificationsApi, authApi } from '../api';
import Avatar from './Avatar';
import { isLocalVisualReview } from '../utils/localReview';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showAppChrome = Boolean(user) || isLocalVisualReview;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  // "Verify your email" banner — resend link on demand, dismissible for the session.
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [dismissedVerify, setDismissedVerify] = useState(false);
  const handleResendVerify = async () => {
    if (resend === 'sending' || resend === 'sent') return;
    setResend('sending');
    try {
      await authApi.requestEmailVerification();
      setResend('sent');
    } catch {
      setResend('idle');
    }
  };

  // Hide the fixed bottom nav while a field is focused. On mobile the on-screen
  // keyboard resizes the viewport and makes a bottom-fixed bar float/jump; this
  // slides it away so it stays put and doesn't obscure inputs.
  useEffect(() => {
    const isField = (el: EventTarget | null) => {
      const t = el as HTMLElement | null;
      return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');
    };
    const nav = () => document.querySelector('.mobile-nav');
    const onIn = (e: FocusEvent) => { if (isField(e.target)) nav()?.classList.add('keyboard-visible'); };
    const onOut = () => nav()?.classList.remove('keyboard-visible');
    window.addEventListener('focusin', onIn);
    window.addEventListener('focusout', onOut);
    return () => { window.removeEventListener('focusin', onIn); window.removeEventListener('focusout', onOut); };
  }, []);

  // Poll the unread notification count so the bell badge stays roughly live.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    let active = true;
    const tick = () => notificationsApi.unreadCount().then((c) => { if (active) setUnread(c); }).catch(() => {});
    tick();
    const t = setInterval(tick, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  return (
    <div className="app-shell bg-gradient-to-br from-brand-950 via-brand-900 to-brand-950 relative mobile-layout">
      {/* Background pattern effect */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0wIDUwSDEwME01MCAwVjEwMCIgc3Ryb2tlPSIjMTY3MzQ3IiBzdHJva2Utd2lkdGg9IjIiLz4KPC9zdmc+')] bg-repeat"></div>
      </div>
      
      <div className="relative z-10 mobile-container">
        <header className="bg-brand-950/88 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <Link to="/" className="flex items-center space-x-2 min-w-0">
                <div className="bg-brand-900 p-2.5 rounded-xl border border-accent-400/35 shrink-0">
                  <GolfIcon name="flag" className="h-7 w-7 text-accent-400" />
                </div>
                <div className="min-w-0">
                  <span className="block text-xl font-bold text-white truncate">Fairplay</span>
                  <div className="hidden sm:block text-[10px] uppercase tracking-[0.16em] text-white/55 font-medium">Championship Golf</div>
                </div>
              </Link>
            {showAppChrome && (
              <div className="flex items-center space-x-1.5 shrink-0">
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    className="p-2.5 text-yellow-400 hover:text-white hover:bg-green-800/50 rounded-full transition-all duration-200 backdrop-blur-sm"
                    title="Admin dashboard"
                    aria-label="Admin dashboard"
                  >
                    <ShieldCheck className="h-5 w-5" />
                  </Link>
                )}
                <Link
                  to="/notifications"
                  className="relative p-2.5 text-green-400 hover:text-white hover:bg-green-800/50 rounded-full transition-all duration-200 backdrop-blur-sm"
                  title="Notifications"
                  aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
                >
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-yellow-500 text-green-950 text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Link>
                <Link
                  to="/messages"
                  className="p-2.5 text-green-400 hover:text-white hover:bg-green-800/50 rounded-full transition-all duration-200 backdrop-blur-sm"
                  title="Messages"
                  aria-label="Messages"
                >
                  <MessageCircle className="h-5 w-5" />
                </Link>
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 bg-gradient-to-r from-green-800/50 to-green-900/50 px-2.5 py-2 rounded-full hover:from-green-700/50 hover:to-green-800/50 transition-all duration-200 border border-green-600/50 backdrop-blur-sm"
                    >
                      <Avatar
                        url={user.avatar_url}
                        name={user.full_name}
                        size="w-8 h-8"
                        bg="bg-gradient-to-br from-green-700 to-green-800"
                        text="text-white text-sm font-bold"
                        className="shadow-lg border border-green-600"
                      />
                      <span className="text-sm font-bold text-white hidden sm:block">
                        {user.full_name?.split(' ')[0]}
                      </span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="p-2.5 text-green-400 hover:text-white hover:bg-green-800/50 rounded-full transition-all duration-200 backdrop-blur-sm"
                      title="Logout"
                      aria-label="Logout"
                    >
                      <LogOut className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <Link to="/review" className="rounded-full border border-accent-400/35 bg-accent-400/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-accent-300">
                    Local review
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {user && !user.is_verified && !dismissedVerify && (
        <div className="bg-yellow-500/15 border-b border-yellow-500/30">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
            <MailWarning className="h-4 w-4 text-yellow-400 shrink-0" />
            {resend === 'sent' ? (
              <span className="text-yellow-100 flex-1">
                Verification email sent — check your inbox (and spam).
              </span>
            ) : (
              <span className="text-yellow-100 flex-1">
                Verify your email to keep your account secure.{' '}
                <button
                  onClick={handleResendVerify}
                  disabled={resend === 'sending'}
                  className="font-semibold text-yellow-300 hover:text-white underline disabled:opacity-60"
                >
                  {resend === 'sending' ? 'Sending…' : 'Resend link'}
                </button>
              </span>
            )}
            <button
              onClick={() => setDismissedVerify(true)}
              className="text-yellow-300/70 hover:text-white text-xs shrink-0"
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-4 pb-24 main-content">
        {children}
      </main>

      {showAppChrome && (
        <nav className="fixed bottom-0 left-0 right-0 bg-brand-950/95 backdrop-blur-lg border-t border-white/10 shadow-2xl z-50 h-20 mobile-nav">
          <div className="max-w-7xl mx-auto px-2 h-full">
            <div className="flex justify-around items-center h-full py-2">
              <Link 
                to="/" 
                className={`flex flex-col items-center py-2 px-4 rounded-2xl transition-all duration-200 ${
                  isActive('/') 
                    ? 'bg-gradient-to-r from-green-800 to-green-900 text-white shadow-lg' 
                    : 'text-green-400 hover:bg-green-800/30'
                }`}
              >
                <Home className="h-6 w-6" strokeWidth={isActive('/') ? 2.5 : 2} />
                <span className="text-xs mt-1 font-bold">Home</span>
              </Link>
              <Link 
                to="/game" 
                className={`flex flex-col items-center py-2 px-4 rounded-2xl transition-all duration-200 ${
                  isActive('/game') 
                    ? 'bg-gradient-to-r from-green-800 to-green-900 text-white shadow-lg' 
                    : 'text-green-400 hover:bg-green-800/30'
                }`}
              >
                <GolfIcon name="trophy" className="h-7 w-7" />
                <span className="text-xs mt-1 font-bold">Games</span>
              </Link>
              <Link 
                to="/quick-game" 
                className="flex items-center justify-center bg-accent-500 text-brand-950 w-14 h-14 rounded-full shadow-xl -mt-7 hover:bg-accent-400 transition-all duration-200 transform hover:scale-105 border-2 border-accent-300/60"
                aria-label="Create a new round"
              >
                <Plus className="h-7 w-7" strokeWidth={2.25} />
              </Link>
              <Link 
                to="/wallet" 
                className={`flex flex-col items-center py-2 px-4 rounded-2xl transition-all duration-200 ${
                  isActive('/wallet') 
                    ? 'bg-gradient-to-r from-green-800 to-green-900 text-white shadow-lg' 
                    : 'text-green-400 hover:bg-green-800/30'
                }`}
              >
                <GolfIcon name="wallet" className="h-7 w-7" />
                <span className="text-xs mt-1 font-bold">Wallet</span>
              </Link>
              <Link 
                to="/profile" 
                className={`flex flex-col items-center py-2 px-4 rounded-2xl transition-all duration-200 ${
                  isActive('/profile') 
                    ? 'bg-gradient-to-r from-green-800 to-green-900 text-white shadow-lg' 
                    : 'text-green-400 hover:bg-green-800/30'
                }`}
              >
                <User className="h-6 w-6" strokeWidth={isActive('/profile') ? 2.5 : 2} />
                <span className="text-xs mt-1 font-bold">Profile</span>
              </Link>
            </div>
          </div>
        </nav>
      )}
      </div>
    </div>
  );
};

export default Layout;
